import React, { useState } from 'react';
import { Wand2, Loader2, BookOpen, Sparkles, ArrowLeft } from 'lucide-react';
import { BlogPost, Theme, Word } from '../types';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';

interface BlogGeneratorPageProps {
  words: Word[];
  theme: Theme;
  onPostGenerated: (post: BlogPost) => void;
}

export default function BlogGeneratorPage({ words, theme, onPostGenerated }: BlogGeneratorPageProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [topic, setTopic] = useState('etymology');
  const [style, setStyle] = useState('educational');
  const [tone, setTone] = useState('professional');
  const navigate = useNavigate();

  const generateBlogPost = async () => {
    setIsGenerating(true);
    try {
      const selectedWords = words
        .sort(() => Math.random() - 0.5)
        .slice(0, 5);

      // Simulated AI-generated content
      const post: BlogPost = {
        id: Date.now().toString(),
        title: `The ${topic === 'etymology' ? 'Origins' : 'Modern Usage'} of "${selectedWords[0].word}"`,
        content: simulateAIContent(selectedWords, topic, style, tone),
        excerpt: `Explore the ${topic === 'etymology' ? 'fascinating history' : 'practical applications'} of "${selectedWords[0].word}" and related terms in this AI-generated analysis.`,
        author: 'LinguaTracker AI',
        date: new Date().toISOString(),
        imageUrl: `https://images.unsplash.com/photo-${Math.floor(Math.random() * 1000)}`,
        tags: [topic, 'AI Generated', 'Vocabulary'],
        relatedWords: selectedWords.map(w => w.word),
        likes: 0,
        comments: []
      };

      onPostGenerated(post);
      toast.success('AI blog post generated successfully!');
    } catch (error) {
      toast.error('Failed to generate blog post');
    } finally {
      setIsGenerating(false);
    }
  };

  const simulateAIContent = (words: Word[], topic: string, style: string, tone: string) => {
    const mainWord = words[0];
    const relatedWords = words.slice(1);

    return `
# ${mainWord.word}: A Deep Dive

${mainWord.definition}

## Historical Context and Etymology
${topic === 'etymology' ? `
The word "${mainWord.word}" has a rich linguistic history, tracing its roots through various languages and cultural contexts. Its evolution reflects the changing nature of language and human understanding.
` : ''}

## Modern Usage and Applications
${mainWord.examples?.map(example => `- "${example}"`).join('\n') || 'Examples coming soon...'}

## Related Words and Concepts
${relatedWords.map(word => `
### ${word.word}
${word.definition}
`).join('\n')}

## Learning Tips
Here are some effective ways to incorporate "${mainWord.word}" into your vocabulary:
1. Study its usage in different contexts
2. Practice using it in conversations
3. Create memorable associations
4. Review regularly using spaced repetition

## Cultural Impact
The term has influenced various fields and continues to evolve in modern discourse.
    `;
  };

  return (
    <div className={`min-h-screen ${
      theme === 'dark' 
        ? 'bg-gray-900 text-gray-100' 
        : 'bg-gray-50 text-gray-900'
    }`}>
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-2 ${
              theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <div className="flex items-center gap-3">
            <Logo theme={theme} size="small" />
            <h1 className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              AI Blog Generator
            </h1>
          </div>
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>

        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-6 rounded-lg ${
              theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            } shadow-lg`}
          >
            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Topic Focus
                </label>
                <select
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg ${
                    theme === 'dark'
                      ? 'bg-gray-700 text-white border-gray-600'
                      : 'bg-white text-gray-900 border-gray-300'
                  }`}
                >
                  <option value="etymology">Word Origins & Etymology</option>
                  <option value="usage">Modern Usage & Examples</option>
                  <option value="learning">Learning Techniques</option>
                  <option value="culture">Cultural Impact</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Writing Style
                </label>
                <select
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg ${
                    theme === 'dark'
                      ? 'bg-gray-700 text-white border-gray-600'
                      : 'bg-white text-gray-900 border-gray-300'
                  }`}
                >
                  <option value="educational">Educational</option>
                  <option value="conversational">Conversational</option>
                  <option value="academic">Academic</option>
                  <option value="storytelling">Storytelling</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Tone
                </label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg ${
                    theme === 'dark'
                      ? 'bg-gray-700 text-white border-gray-600'
                      : 'bg-white text-gray-900 border-gray-300'
                  }`}
                >
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="humorous">Humorous</option>
                  <option value="formal">Formal</option>
                </select>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={generateBlogPost}
                disabled={isGenerating || words.length < 3}
                className={`w-full py-3 rounded-lg flex items-center justify-center gap-2 ${
                  theme === 'dark'
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                } ${(isGenerating || words.length < 3) && 'opacity-50 cursor-not-allowed'}`}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 size={20} />
                    Generate AI Blog Post
                  </>
                )}
              </motion.button>

              {words.length < 3 && (
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Add at least 3 words to your collection to generate blog posts.
                </p>
              )}

              <div className={`mt-4 p-4 rounded-lg ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-purple-50'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen size={16} className="text-purple-500" />
                  <span className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    How it works
                  </span>
                </div>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Our AI analyzes your vocabulary collection and generates engaging blog posts about selected words, their meanings, and usage. Customize the topic, style, and tone to create unique content.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}